import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { fetchUserFromFirestore } from '../utils/firebase/user';
import { auth } from '../utils/firebase/firebase.utils';
import { setUser, clearUserCache } from '../store/user/user.action';
import { clearUserCache as clearLocalUserCache, saveUserCache } from '../utils/cache/userCache';

/**
 * Custom hook for setting usernames
 * @returns {Object} Hook utilities and state
 */
export const useSetUsername = () => {
  const dispatch = useDispatch();
  const [isSettingUsername, setIsSettingUsername] = useState(false);
  const [error, setError] = useState(null);
  const functions = getFunctions();
  const setUsernameFunction = httpsCallable(functions, 'setUsername');

  /**
   * Set or update a username for the current user
   * @param {string} username - The desired username (3-20 chars, lowercase letters, numbers, underscores only)
   * @returns {Promise<Object>} Result object with success status and message
   */
  const setUsername = async (username) => {
    if (!username || username.trim() === '') {
      const errorMsg = 'Username cannot be empty';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    // Client-side validation
    const usernameRegex = /^[a-z0-9_]{3,20}$/;
    if (!usernameRegex.test(username)) {
      const errorMsg = 'Username must be 3-20 characters long and contain only lowercase letters, numbers, and underscores';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    setIsSettingUsername(true);
    setError(null);
    
    try {
      const result = await setUsernameFunction({ username });
      const { success, message } = result.data;
      
      if (success) {
        clearLocalUserCache(auth.currentUser.uid);
        dispatch(clearUserCache());
        
        // Brief delay so Firestore write is visible
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const userDoc = await fetchUserFromFirestore(auth.currentUser.uid);
        
        if (userDoc) {
          let role = 'user';
          try {
            const tokenResult = await auth.currentUser.getIdTokenResult(true);
            role = tokenResult?.claims?.role || 'user';
          } catch (err) {
            // Could not get role from token
          }
          
          const completeUserData = {
            ...userDoc,
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
            role,
          };
          dispatch(setUser(completeUserData));
          saveUserCache(completeUserData, auth.currentUser.uid);
        }
      }
      
      setIsSettingUsername(false);
      return { success, message, username };
    } catch (err) {
      let errorMessage = 'Error setting username. Please try again.';
      
      // Handle specific Firebase error codes
      if (err.code === 'functions/already-exists') {
        errorMessage = 'Username is already taken. Please choose another.';
      } else if (err.code === 'functions/invalid-argument') {
        errorMessage = 'Invalid username format. Use 3-20 lowercase letters, numbers, or underscores.';
      } else if (err.code === 'functions/unauthenticated') {
        errorMessage = 'You must be signed in to set a username.';
      } else if (err.code === 'functions/not-found' || err.code === 'functions/unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setIsSettingUsername(false);
      return { success: false, error: errorMessage };
    }
  };

  return {
    setUsername,
    isSettingUsername,
    error
  };
};

